import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  getUserTickets,
  getTicketCategories,
  createTicket,
  getTicketDetails,
  addTicketMessage,
} from '../services/api';
import { useToast } from '../components/ToastContainer';
import './Tickets.css';

const icons = {
  ticket: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 002 2 2 2 0 010 4 2 2 0 00-2 2v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 00-2-2 2 2 0 010-4 2 2 0 002-2V7a2 2 0 00-2-2H5z" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  send: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  attachment: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  bolt: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
    </svg>
  ),
  bill: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 8h10M7 12h6M7 16h8" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v16H4z" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  ),
};

const STATUS_META = {
  open: { labelKey: 'tickets.open', fallback: 'Open', tone: 'info' },
  in_process: { labelKey: 'tickets.inProcess', fallback: 'In process', tone: 'warn' },
  solved: { labelKey: 'tickets.solved', fallback: 'Solved', tone: 'ok' },
  declined: { labelKey: 'tickets.declined', fallback: 'Declined', tone: 'bad' },
};

const PRIORITY_META = {
  low: { labelKey: 'tickets.priorityLow', fallback: 'Low' },
  normal: { labelKey: 'tickets.priorityNormal', fallback: 'Normal' },
  high: { labelKey: 'tickets.priorityHigh', fallback: 'High' },
  urgent: { labelKey: 'tickets.priorityUrgent', fallback: 'Urgent' },
};

const emptyTicketForm = {
  category_id: null,
  subject: '',
  message: '',
  priority: 'normal',
  attachment_name: '',
  attachment_data: '',
  attachment_type: '',
};

const Tickets = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const messagesEndRef = useRef(null);

  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newTicket, setNewTicket] = useState(emptyTicketForm);
  const [submitting, setSubmitting] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [messageAttachment, setMessageAttachment] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);

  const quickTopics = useMemo(
    () => [
      {
        key: 'connect',
        icon: icons.bolt,
        title: t('tickets.topicConnectTitle'),
        hint: t('tickets.topicConnectHint'),
        subject: t('tickets.topicConnectSubject'),
      },
      {
        key: 'account',
        icon: icons.shield,
        title: t('tickets.topicAccountTitle'),
        hint: t('tickets.topicAccountHint'),
        subject: t('tickets.topicAccountSubject'),
      },
      {
        key: 'billing',
        icon: icons.bill,
        title: t('tickets.topicBillingTitle'),
        hint: t('tickets.topicBillingHint'),
        subject: t('tickets.topicBillingSubject'),
      },
    ],
    [t]
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      openTicketDetail(parseInt(id, 10), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTicket?.messages) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTicket?.messages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ticketsData, categoriesData] = await Promise.all([
        getUserTickets('all'),
        getTicketCategories(),
      ]);
      setTickets(ticketsData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const base = { all: tickets.length, open: 0, in_process: 0, solved: 0, declined: 0 };
    tickets.forEach((ticket) => {
      if (base[ticket.status] !== undefined) base[ticket.status] += 1;
    });
    return base;
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tickets.filter((ticket) => {
      const statusOk = statusFilter === 'all' || ticket.status === statusFilter;
      if (!statusOk) return false;
      if (!q) return true;
      return (
        ticket.subject?.toLowerCase().includes(q) ||
        ticket.ticket_number?.toLowerCase().includes(q) ||
        ticket.category_name?.toLowerCase().includes(q)
      );
    });
  }, [tickets, query, statusFilter]);

  const handleFileSelect = (e, isNewTicket = true) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showError(t('tickets.fileTooLarge', 'File too large. Maximum size is 2MB'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      if (isNewTicket) {
        setNewTicket((prev) => ({
          ...prev,
          attachment_name: file.name,
          attachment_data: base64,
          attachment_type: file.type,
        }));
      } else {
        setMessageAttachment({ name: file.name, data: base64, type: file.type });
      }
    };
    reader.readAsDataURL(file);
  };

  const openComposer = (preset = {}) => {
    setNewTicket({
      ...emptyTicketForm,
      subject: preset.subject || '',
      message: preset.message || '',
      category_id: preset.category_id || null,
      priority: preset.priority || 'normal',
    });
    setShowNewTicketModal(true);
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      showError(t('tickets.fillRequired', 'Please fill in subject and message'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await createTicket(newTicket);
      showSuccess(
        t('tickets.created', 'Ticket created successfully! Ticket #{{number}}', {
          number: response.ticket_number,
        })
      );
      setShowNewTicketModal(false);
      setNewTicket(emptyTicketForm);
      await loadData();
      if (response.ticket_id) {
        openTicketDetail(response.ticket_id);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openTicketDetail = async (ticketId, syncUrl = true) => {
    setDetailLoading(true);
    try {
      const detail = await getTicketDetails(ticketId);
      setActiveTicket(detail);
      if (syncUrl) {
        setSearchParams({ id: String(ticketId) });
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeTicketDetail = () => {
    setActiveTicket(null);
    setNewMessage('');
    setMessageAttachment(null);
    setSearchParams({});
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeTicket) return;
    setSendingMessage(true);
    try {
      await addTicketMessage(activeTicket.id, {
        message: newMessage,
        attachment_name: messageAttachment?.name || '',
        attachment_data: messageAttachment?.data || '',
        attachment_type: messageAttachment?.type || '',
      });
      setNewMessage('');
      setMessageAttachment(null);
      const detail = await getTicketDetails(activeTicket.id);
      setActiveTicket(detail);
      loadData();
    } catch (error) {
      showError(error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelative = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('tickets.justNow');
    if (mins < 60) return t('tickets.minsAgo', { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('tickets.hoursAgo', { n: hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return t('tickets.daysAgo', { n: days });
    return formatDate(dateString);
  };

  const getCategoryOptions = () => {
    const options = [];
    categories.forEach((cat) => {
      options.push({ id: cat.id, name: cat.name, isParent: true });
      if (cat.children?.length) {
        cat.children.forEach((child) => {
          options.push({ id: child.id, name: `— ${child.name}`, isParent: false });
        });
      }
    });
    return options;
  };

  const statusLabel = (status) => {
    const meta = STATUS_META[status];
    return meta ? t(meta.labelKey, meta.fallback) : status;
  };

  const filters = [
    { key: 'all', label: t('tickets.allTickets', 'All'), count: stats.all },
    { key: 'open', label: t('tickets.open', 'Open'), count: stats.open },
    { key: 'in_process', label: t('tickets.inProcess', 'In process'), count: stats.in_process },
    { key: 'solved', label: t('tickets.solved', 'Solved'), count: stats.solved },
  ];

  const canReply = activeTicket && (activeTicket.status === 'open' || activeTicket.status === 'in_process');

  return (
    <div className={`support-page ${activeTicket ? 'has-thread' : ''}`}>
      <div className="support-content">
      <section className="support-hero">
        <div className="support-hero-copy">
          <p className="support-kicker">{t('tickets.kicker', 'Support')}</p>
          <h1>{t('tickets.heroTitle', 'How can we help?')}</h1>
          <p className="support-lead">
            {t(
              'tickets.heroLead',
              'Open a ticket for connection, account, or billing issues. Our team replies in this thread.'
            )}
          </p>
          <p className="support-lead-mobile">
            {t('tickets.leadMobile', 'Open a ticket or pick one below to continue.')}
          </p>
          <div className="support-hero-actions">
            <button type="button" className="support-btn primary" onClick={() => openComposer()}>
              {icons.plus}
              <span>{t('tickets.newTicket', 'New ticket')}</span>
            </button>
            <a className="support-btn ghost" href="mailto:support@arcvpn.com">
              {icons.mail}
              <span className="support-mail-label">support@arcvpn.com</span>
              <span className="support-mail-label-short">{t('tickets.emailShort', 'Email')}</span>
            </a>
          </div>
        </div>
        <div className="support-hero-stats" aria-label="Ticket summary">
          <div className="support-stat">
            <strong>{stats.open + stats.in_process}</strong>
            <span>{t('tickets.active', 'Active')}</span>
          </div>
          <div className="support-stat">
            <strong>{stats.solved}</strong>
            <span>{t('tickets.solved', 'Solved')}</span>
          </div>
          <div className="support-stat">
            <strong>{stats.all}</strong>
            <span>{t('tickets.total', 'Total')}</span>
          </div>
        </div>
      </section>

      <section className="support-topics">
        {quickTopics.map((topic) => (
          <button
            key={topic.key}
            type="button"
            className="support-topic"
            onClick={() =>
              openComposer({
                subject: topic.subject,
                message: `${topic.hint}\n\n${t('tickets.detailsLabel', 'Details:')}\n`,
              })
            }
          >
            <span className="support-topic-icon">{topic.icon}</span>
            <span className="support-topic-copy">
              <strong>{topic.title}</strong>
              <em>{topic.hint}</em>
            </span>
          </button>
        ))}
      </section>

      <section className="support-workspace">
        <aside className={`support-list-pane ${activeTicket ? 'is-hidden-mobile' : ''}`}>
          <div className="support-list-toolbar">
            <div className="support-filters" role="tablist">
              {filters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === filter.key}
                  className={`support-filter ${statusFilter === filter.key ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter(filter.key)}
                >
                  {filter.label}
                  <span>{filter.count}</span>
                </button>
              ))}
            </div>
            <label className="support-search">
              <span className="support-search-icon">{icons.search}</span>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('tickets.search', 'Search tickets…')}
              />
            </label>
          </div>

          {loading ? (
            <div className="support-empty">
              <div className="support-spinner" />
              <p>{t('tickets.loading', 'Loading tickets…')}</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="support-empty">
              <span className="support-empty-icon">{icons.chat}</span>
              <h3>{t('tickets.noTickets', 'No tickets yet')}</h3>
              <p>{t('tickets.noTicketsDesc', 'Create a ticket and we will follow up in this inbox.')}</p>
              <button type="button" className="support-btn primary" onClick={() => openComposer()}>
                {icons.plus}
                <span>{t('tickets.createFirst', 'Create your first ticket')}</span>
              </button>
            </div>
          ) : (
            <ul className="support-ticket-list">
              {filteredTickets.map((ticket) => {
                const meta = STATUS_META[ticket.status] || STATUS_META.open;
                return (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      className={`support-ticket-item ${activeTicket?.id === ticket.id ? 'is-active' : ''}`}
                      onClick={() => openTicketDetail(ticket.id)}
                    >
                      <div className="support-ticket-top">
                        <span className="support-ticket-id">#{ticket.ticket_number}</span>
                        <span className={`support-chip tone-${meta.tone}`}>{statusLabel(ticket.status)}</span>
                      </div>
                      <strong className="support-ticket-subject">{ticket.subject}</strong>
                      <div className="support-ticket-meta">
                        {ticket.category_name && <span>{ticket.category_name}</span>}
                        <span>
                          {ticket.message_count} {t('tickets.messages', 'messages')}
                        </span>
                        <span>{formatRelative(ticket.last_message || ticket.updated_at || ticket.created_at)}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <div className={`support-thread-pane ${activeTicket ? 'is-open' : ''}`}>
          {!activeTicket && !detailLoading ? (
            <div className="support-thread-placeholder">
              <span className="support-empty-icon">{icons.ticket}</span>
              <h3>{t('tickets.selectTicket', 'Select a ticket')}</h3>
              <p>
                {t(
                  'tickets.selectTicketDesc',
                  'Choose a conversation on the left, or open a new request.'
                )}
              </p>
            </div>
          ) : detailLoading && !activeTicket ? (
            <div className="support-empty">
              <div className="support-spinner" />
            </div>
          ) : (
            <>
              <header className="support-thread-header">
                <button type="button" className="support-icon-btn mobile-only" onClick={closeTicketDetail}>
                  {icons.back}
                </button>
                <div className="support-thread-title">
                  <div className="support-thread-idrow">
                    <span>#{activeTicket.ticket_number}</span>
                    <span className={`support-chip tone-${STATUS_META[activeTicket.status]?.tone || 'info'}`}>
                      {statusLabel(activeTicket.status)}
                    </span>
                    {activeTicket.priority && (
                      <span className="support-chip tone-neutral">
                        {t(
                          PRIORITY_META[activeTicket.priority]?.labelKey || 'tickets.priorityNormal',
                          PRIORITY_META[activeTicket.priority]?.fallback || activeTicket.priority
                        )}
                      </span>
                    )}
                  </div>
                  <h2>{activeTicket.subject}</h2>
                  {activeTicket.category_name && (
                    <p className="support-thread-category">{activeTicket.category_name}</p>
                  )}
                </div>
                <button type="button" className="support-icon-btn" onClick={closeTicketDetail} aria-label="Close">
                  {icons.close}
                </button>
              </header>

              <div className="support-messages">
                {activeTicket.messages?.map((msg) => (
                  <article
                    key={msg.id}
                    className={`support-message ${msg.is_admin_reply ? 'from-support' : 'from-user'}`}
                  >
                    <div className="support-message-meta">
                      <strong>{msg.is_admin_reply ? t('tickets.supportTeam', 'ArcVPN Support') : msg.username}</strong>
                      <time>{formatDate(msg.created_at)}</time>
                    </div>
                    <div className="support-message-body">{msg.message}</div>
                    {msg.attachment_name && (
                      <div className="support-attachment">
                        {icons.attachment}
                        <span>{msg.attachment_name}</span>
                      </div>
                    )}
                  </article>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {canReply ? (
                <form className="support-composer" onSubmit={handleSendMessage}>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('tickets.replyPlaceholder', 'Write a reply…')}
                    rows={3}
                  />
                  <div className="support-composer-bar">
                    <label className="support-attach">
                      {icons.attachment}
                      <span>{t('tickets.attach', 'Attach')}</span>
                      <input
                        type="file"
                        onChange={(e) => handleFileSelect(e, false)}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        hidden
                      />
                    </label>
                    {messageAttachment && (
                      <div className="support-file-pill">
                        <span>{messageAttachment.name}</span>
                        <button type="button" onClick={() => setMessageAttachment(null)}>
                          {icons.close}
                        </button>
                      </div>
                    )}
                    <button
                      type="submit"
                      className="support-btn primary"
                      disabled={sendingMessage || !newMessage.trim()}
                    >
                      {icons.send}
                      <span>{sendingMessage ? t('common.sending', 'Sending…') : t('tickets.send', 'Send')}</span>
                    </button>
                  </div>
                </form>
              ) : (
                <div className="support-closed">
                  <p>
                    {t(
                      'tickets.closedNotice',
                      'This ticket is closed. Open a new ticket if you still need help.'
                    )}
                  </p>
                  <button type="button" className="support-btn primary" onClick={() => openComposer()}>
                    {icons.plus}
                    <span>{t('tickets.newTicket', 'New ticket')}</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      </div>

      {showNewTicketModal && (
        <div className="support-modal-overlay" onClick={() => setShowNewTicketModal(false)}>
          <div className="support-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="support-modal-header">
              <div>
                <p className="support-kicker">{t('tickets.createTicket', 'Create ticket')}</p>
                <h2>{t('tickets.createTicketTitle', 'Tell us what happened')}</h2>
              </div>
              <button type="button" className="support-icon-btn" onClick={() => setShowNewTicketModal(false)}>
                {icons.close}
              </button>
            </header>
            <form onSubmit={handleCreateTicket} className="support-modal-form">
              <label className="support-field">
                <span>{t('tickets.category', 'Category')}</span>
                <select
                  value={newTicket.category_id || ''}
                  onChange={(e) =>
                    setNewTicket((prev) => ({
                      ...prev,
                      category_id: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                >
                  <option value="">{t('tickets.selectCategory', 'Select a category')}</option>
                  {getCategoryOptions().map((cat) => (
                    <option
                      key={cat.id}
                      value={cat.id}
                      disabled={
                        cat.isParent && categories.find((c) => c.id === cat.id)?.children?.length > 0
                      }
                    >
                      {cat.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="support-field">
                <span>{t('tickets.subject', 'Subject')} *</span>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket((prev) => ({ ...prev, subject: e.target.value }))}
                  placeholder={t('tickets.subjectPlaceholder', 'Brief description of your issue')}
                  required
                />
              </label>
              <label className="support-field">
                <span>{t('tickets.priority', 'Priority')}</span>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket((prev) => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="low">{t('tickets.priorityLow', 'Low')}</option>
                  <option value="normal">{t('tickets.priorityNormal', 'Normal')}</option>
                  <option value="high">{t('tickets.priorityHigh', 'High')}</option>
                  <option value="urgent">{t('tickets.priorityUrgent', 'Urgent')}</option>
                </select>
              </label>
              <label className="support-field">
                <span>{t('tickets.message', 'Message')} *</span>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder={t('tickets.messagePlaceholder', 'Describe your issue in detail…')}
                  rows={6}
                  required
                />
              </label>
              <div className="support-field">
                <span>{t('tickets.attachment', 'Attachment')} (max 2MB)</span>
                <label className="support-file-drop">
                  <input
                    type="file"
                    onChange={(e) => handleFileSelect(e, true)}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  {newTicket.attachment_name ? (
                    <span>{newTicket.attachment_name}</span>
                  ) : (
                    <span>{t('tickets.chooseFile', 'Choose a file')}</span>
                  )}
                </label>
                {newTicket.attachment_name && (
                  <button
                    type="button"
                    className="support-clear-file"
                    onClick={() =>
                      setNewTicket((prev) => ({
                        ...prev,
                        attachment_name: '',
                        attachment_data: '',
                        attachment_type: '',
                      }))
                    }
                  >
                    {t('common.remove', 'Remove')}
                  </button>
                )}
              </div>
              <div className="support-modal-actions">
                <button type="button" className="support-btn ghost" onClick={() => setShowNewTicketModal(false)}>
                  {t('common.cancel', 'Cancel')}
                </button>
                <button type="submit" className="support-btn primary" disabled={submitting}>
                  {submitting ? t('common.submitting', 'Submitting…') : t('tickets.submit', 'Submit ticket')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
